<template>
  <Listbox
    v-slot="{ open }"
    :model-value="modelValue"
    as="div"
    class="space-y-1"
    :disabled="disabled"
    @update:model-value="
      (value: string | number | null) => {
        $emit('update:modelValue', value);
      }
    "
  >
    <ListboxLabel v-if="label !== null" :class="labelClass">
      {{ label }}
    </ListboxLabel>
    <div class="relative" :class="dropdownClass">
      <span class="inline-block w-full">
        <ListboxButton
          ref="reference"
          class="w-full rounded-md bg-white py-3 pr-10 pl-3 text-left text-sm text-gray-700 shadow ring-1 ring-gray-400 transition duration-150 ease-in-out focus:ring-2 focus:ring-purple-300 focus:outline-none"
          :class="[
            buttonClass,
            disabled
              ? 'cursor-not-allowed text-gray-500 opacity-75'
              : 'cursor-pointer opacity-100',
          ]"
        >
          <div class="flex h-full items-center space-x-3">
            <Icon
              v-if="options.usesImages && selectedItem.image"
              :name="selectedItem.image"
              class="size-5"
              :class="selectedItem.iconColor"
            />
            <CountryFlag
              v-if="selectedItem.country"
              :country="selectedItem.country.alpha3"
              size="small"
            />
            <div class="flex w-full flex-col">
              <span class="block truncate">
                {{ selectedItem.label }}
              </span>
              <span
                v-if="options.usesSubLabel"
                class="block truncate text-xs text-gray-700"
              >
                {{ selectedItem.subLabel }}
              </span>
            </div>
          </div>
          <span
            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
          >
            <Icon
              name="angle-down"
              class="inline h-4 w-5 transition duration-250"
              :class="open ? 'rotate-180' : 'rotate-0'"
            />
          </span>
        </ListboxButton>
      </span>

      <Teleport to="body">
        <div ref="floating" data-floating-menu :style="style">
          <Transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <ListboxOptions
              class="custom-table-scroll max-h-56 overflow-auto rounded-md bg-white text-sm shadow-lg ring-1 ring-gray-400 focus:outline-none"
              :class="panelClass"
            >
              <ListboxOption
                v-for="item in items"
                :key="item.label ?? undefined"
                v-slot="{ active, selected }"
                :value="item.value"
                as="template"
                class="focus:outline-none"
              >
                <li
                  class="relative cursor-pointer py-2 select-none first:rounded-t-md last:rounded-b-md"
                  :class="{
                    'bg-burgundy-50': active,
                    [panelListClass || 'text-gray-700']: true,
                  }"
                >
                  <div class="flex items-center space-x-3 px-3">
                    <Icon
                      v-if="options.usesImages && item.image"
                      :name="item.image"
                      class="h-5 w-6"
                      :class="item.iconColor"
                    />
                    <CountryFlag
                      v-if="item.country"
                      :country="item.country.alpha3"
                      size="small"
                    />
                    <div class="flex flex-col">
                      <span class="block truncate">
                        {{ item.label }}
                      </span>
                      <span
                        v-if="options.usesSubLabel"
                        class="block truncate text-xs text-gray-700"
                      >
                        {{ item.subLabel }}
                      </span>
                    </div>
                    <Icon
                      v-if="selected && useCheckMark"
                      name="check"
                      class="inline size-3 text-purple-700 lg:h-4 lg:w-5"
                    />
                  </div>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </Transition>
        </div>
      </Teleport>
    </div>
  </Listbox>
</template>

<script setup lang="ts">
import { computed, PropType, DeepReadonly } from "vue";
import { Placement } from "@floating-ui/vue";
import * as lodash from "lodash-es";
import Icon from "@/components/Core/Icon.vue";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
  ListboxLabel,
} from "@headlessui/vue";
import { DropdownItem, DropdownOptions } from "@/components/Forms";
import { useFloating } from "@/utils/components";
import CountryFlag from "vue-country-flag-next";

const props = defineProps({
  label: {
    type: String as PropType<string | null>,
    default: null,
  },
  items: {
    type: Array as PropType<(DeepReadonly<DropdownItem> | DropdownItem)[]>,
    default: () => [],
  },
  modelValue: {
    type: [String, Number, Object] as PropType<
      string | number | undefined | Record<string, unknown>
    >,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object as PropType<DropdownOptions>,
    default: () => ({
      defaultLabel: null,
      usesImages: false,
      usesSubLabel: false,
    }),
  },
  labelClass: {
    type: String,
    default: "block text-sm leading-5 font-medium text-gray-700",
  },
  dropdownClass: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  buttonClass: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  panelClass: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  panelPosition: {
    type: String as PropType<Placement | undefined>,
    default: undefined,
  },
  panelListClass: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  useCheckMark: {
    type: Boolean,
    default: false,
  },
});
defineEmits({ "update:modelValue": (_value: unknown) => true });

const defaultNullItem = computed(() => {
  return {
    image: props.options.usesImages ? null : null,
    label:
      props.options.defaultLabel !== null
        ? props.options.defaultLabel
        : "Choose an item",
    value: null,
    subLabel: null,
    country: null,
  };
});

const selectedItem = computed(() => {
  return (
    props.items.find((item) => lodash.isEqual(item.value, props.modelValue)) ??
    defaultNullItem.value
  );
});

const {
  reference,
  floating,
  style: baseStyle,
} = useFloating({
  strategy: "fixed",
  placement: computed(() => props.panelPosition),
});

const style = computed(() => {
  const s = { ...baseStyle.value };
  if (reference.value) {
    const el =
      "$el" in reference.value
        ? (reference.value.$el as HTMLElement)
        : (reference.value as unknown as HTMLElement);
    s.width = `${el.offsetWidth}px`;
  }
  return s;
});
</script>
